import { loadDatadog } from "@/lib/analytics/datadog";

/** Vite ``/assets/{entry}.{hash}.css`` and legacy ``/index.{hash}.css`` bundle links. */
const LEGACY_INDEX_STYLESHEET_HREF = /\/index[.-][A-Za-z0-9]+\.css(?:\?|$)/i;
const ENTRY_MODULE_SRC =
  /\/assets\/([A-Za-z0-9_-]+)[.-][A-Za-z0-9]+\.js(?:\?|$)/i;

const entryStylesheetHrefPattern = (entryName: string): RegExp =>
  new RegExp(`/assets/${entryName}[.-][A-Za-z0-9]+\\.css(?:\\?|$)`, "i");

const toAbsoluteStylesheetUrl = (href: string): string | null => {
  try {
    return new URL(href, document.baseURI).href;
  } catch {
    return null;
  }
};

const resolveEntryNameFromModuleScript = (): string | null => {
  for (const script of document.querySelectorAll<HTMLScriptElement>(
    'script[type="module"][src]',
  )) {
    const src = script.getAttribute("src");
    if (!src) {
      continue;
    }
    const match = ENTRY_MODULE_SRC.exec(src);
    if (match?.[1]) {
      return match[1];
    }
  }
  return null;
};

const reportUnresolvedTailwindUrl = (
  reason: string,
  context?: Record<string, string | number | boolean | undefined>,
): void => {
  if (import.meta.env.DEV) {
    console.warn("[resolveHeraldPdfTailwindStylesheetUrl]", reason, context);
  }

  loadDatadog().then((datadogRum) => {
    datadogRum.addAction("herald_pdf_tailwind_url_unresolved", {
      reason,
      ...context,
    });
  });
};

const findStylesheetUrl = (pattern: RegExp): string | null => {
  for (const link of document.querySelectorAll<HTMLLinkElement>(
    'link[rel="stylesheet"]',
  )) {
    const href = link.getAttribute("href");
    if (!href || !pattern.test(href)) {
      continue;
    }
    const absoluteUrl = toAbsoluteStylesheetUrl(href);
    if (absoluteUrl !== null) {
      return absoluteUrl;
    }
  }
  return null;
};

/**
 * Returns the absolute URL of the active naapp Tailwind bundle, if present in ``<head>``.
 * Used by {@link useDownloadHtmlAsPdf} so naapi can fetch the same CSS the user loaded.
 */
export const resolveHeraldPdfTailwindStylesheetUrl = (): string | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const entryName = resolveEntryNameFromModuleScript();
  if (entryName !== null) {
    const entryBundleUrl = findStylesheetUrl(
      entryStylesheetHrefPattern(entryName),
    );
    if (entryBundleUrl !== null) {
      return entryBundleUrl;
    }
  }

  const legacyIndexUrl = findStylesheetUrl(LEGACY_INDEX_STYLESHEET_HREF);
  if (legacyIndexUrl !== null) {
    return legacyIndexUrl;
  }

  reportUnresolvedTailwindUrl("no_matching_stylesheet_link", {
    entry_name: entryName ?? undefined,
    stylesheet_link_count: document.querySelectorAll('link[rel="stylesheet"]')
      .length,
  });
  return null;
};
